import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PaymentsIcon from "@mui/icons-material/Payments";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Shell } from "../../common/shell/shell.page";
import { PageHeader } from "../../common/components/page-header";
import { LoadingOverlay } from "../../common/components/loading-overlay";
import application from "../../core/application";
import { Permission } from "../../users/user.entity";
import { SwalReact } from "../../common/swal-react";
import ValueObjectObservable from "../../common/vo/value-object-observable";
import { MoneyInputValueObject } from "../../common/components/money-input-value-object";
import { Customer } from "../customer.entity";
import { customerRepository } from "../customer.repository";
import { CustomerAccount } from "../customer-account.entity";
import { customerAccountRepository } from "../customer-account.repository";
import { CreditTransaction } from "../credit-transaction.entity";
import { creditTransactionRepository } from "../credit-transaction.repository";
import { customerCreditService } from "../customer-credit.service";
import { formatBRL } from "../../sell/utils/sales-analytics";
import { useModuleAccess } from "../../settings/module-access";
import { ModuleLockedView } from "../../settings/components/module-locked-view";

export const CustomerDetailPage = () => {
  application.useHasPermission(Permission.MANAGE_CUSTOMERS);
  const { customerId } = useParams<{ customerId: string }>();
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [account, setAccount] = useState<CustomerAccount | null>(null);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const moduleAccess = useModuleAccess();

  const load = useCallback(async () => {
    if (!customerId) {
      setCustomer(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const c = await customerRepository.findById(customerId);
      setCustomer(c);
    } catch (error) {
      console.error("Falha ao carregar cliente.", error);
      setCustomer(null);
      setLoading(false);
      return;
    }

    try {
      const a = await customerAccountRepository.getByCustomerId(customerId);
      setAccount(a);
    } catch (error) {
      console.error("Falha ao carregar conta do cliente.", error);
      setAccount(null);
    }

    try {
      const tx = await creditTransactionRepository.listByCustomerId(customerId);
      setTransactions(tx);
    } catch (error) {
      console.error("Falha ao carregar histórico do cliente.", error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const registerPayment = async () => {
    if (!customerId) return;
    const balance = account?.getBalance() ?? 0;
    const amount = new ValueObjectObservable(0);
    const result = await SwalReact.fire({
      title: "Registrar pagamento",
      html: (
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Saldo atual: {formatBRL(balance)}
          </Typography>
          <MoneyInputValueObject
            title="Valor pago"
            valueObject={amount}
            textFieldProps={{ fullWidth: true, autoFocus: true }}
          />
        </Stack>
      ),
      showCancelButton: true,
      confirmButtonText: "Registrar",
      cancelButtonText: "Cancelar",
      preConfirm: async () => {
        if (amount.get() <= 0) {
          SwalReact.showValidationMessage("Informe um valor maior que zero.");
          return false;
        }
        if (amount.get() > balance) {
          SwalReact.showValidationMessage("Pagamento maior que o saldo devedor.");
          return false;
        }
        return true;
      },
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !SwalReact.isLoading(),
    });

    if (!result.isConfirmed) return;
    await customerCreditService.registerManualPayment(customerId, amount.get());
    await load();
  };

  if (loading || moduleAccess.loading) {
    return (
      <Shell title="Cliente">
        <LoadingOverlay message="Carregando cliente…" subtle />
      </Shell>
    );
  }

  if (!moduleAccess.activeCustomersTier) {
    return (
      <Shell title="Cliente">
        <PageHeader title="Clientes" description="Cadastro de clientes, fiado e pagamentos." />
        <ModuleLockedView
          moduleName="Clientes e Fiado"
          groupId="customers_credit"
          details="Ative este módulo para consultar clientes, saldos e histórico de fiado."
          features={["Cadastro de clientes", "Venda fiado no PDV", "Saldo devedor e histórico", "Registro de pagamentos"]}
        />
      </Shell>
    );
  }

  if (!customer) {
    return (
      <Shell title="Cliente não encontrado">
        <PageHeader
          title="Cliente não encontrado"
          description="Não foi possível localizar este cliente."
          action={
            <Button component={RouterLink} to="/clientes" startIcon={<ArrowBackIcon />}>
              Voltar
            </Button>
          }
        />
      </Shell>
    );
  }

  const balance = account?.getBalance() ?? 0;
  const totalDebt = transactions
    .filter((transaction) => transaction.getType() === "debt")
    .reduce((sum, transaction) => sum + transaction.getAmount(), 0);
  const totalPaid = transactions
    .filter((transaction) => transaction.getType() === "payment")
    .reduce((sum, transaction) => sum + transaction.getAmount(), 0);

  return (
    <Shell title="Cliente">
      <PageHeader
        title={customer.getName()}
        description={customer.getPhone() || "Telefone não informado"}
        action={
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button component={RouterLink} to="/clientes" startIcon={<ArrowBackIcon />}>
              Clientes
            </Button>
            <Button
              variant="contained"
              startIcon={<PaymentsIcon />}
              onClick={() => registerPayment()}
              disabled={balance <= 0}
            >
              Registrar pagamento
            </Button>
          </Stack>
        }
      />

      <Stack spacing={3}>
        <Card
          elevation={0}
          sx={{
            border: 1,
            borderColor: (theme) =>
              balance > 0 ? alpha(theme.palette.warning.main, 0.28) : alpha(theme.palette.primary.main, 0.22),
            overflow: "hidden",
            background: (theme) =>
              `linear-gradient(135deg, ${alpha(
                balance > 0 ? theme.palette.warning.main : theme.palette.primary.main,
                0.13
              )} 0%, ${theme.palette.background.paper} 72%)`,
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
            <Stack direction={{ xs: "column", lg: "row" }} spacing={3}>
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 2,
                      bgcolor: balance > 0 ? "warning.main" : "primary.main",
                      color: "primary.contrastText",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AccountBalanceWalletIcon />
                  </Box>
                  <Box>
                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.08 }}>
                      Conta do cliente
                    </Typography>
                    <Typography variant="h4" fontWeight={900} color={balance > 0 ? "warning.dark" : "primary.dark"}>
                      {formatBRL(balance)}
                    </Typography>
                  </Box>
                </Stack>
                {balance > 0 ? <Chip color="warning" label="Cliente com dívida em aberto" /> : <Chip color="success" label="Sem saldo devedor" />}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, maxWidth: 520 }}>
                  {customer.getNotes() || "Sem observações cadastradas para este cliente."}
                </Typography>
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ flex: 1.1 }}>
                <Box sx={{ flex: 1, p: 2, borderRadius: 2, bgcolor: "background.paper", border: 1, borderColor: "divider" }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TrendingUpIcon color="warning" />
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      Total vendido fiado
                    </Typography>
                  </Stack>
                  <Typography variant="h6" fontWeight={900} sx={{ mt: 0.75 }}>
                    {formatBRL(totalDebt)}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, p: 2, borderRadius: 2, bgcolor: "background.paper", border: 1, borderColor: "divider" }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TrendingDownIcon color="success" />
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      Total pago
                    </Typography>
                  </Stack>
                  <Typography variant="h6" fontWeight={900} sx={{ mt: 0.75 }}>
                    {formatBRL(totalPaid)}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ border: 1, borderColor: "divider" }}>
          <CardContent>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
              Histórico
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell align="right">Valor</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>{transaction.getCreatedAt().format("DD/MM/YYYY HH:mm")}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={transaction.getType() === "debt" ? "warning" : "success"}
                          label={transaction.getType() === "debt" ? "Dívida" : "Pagamento"}
                        />
                      </TableCell>
                      <TableCell>
                        {transaction.getSaleId() ? (
                          <Button
                            component={RouterLink}
                            to={`/vendas/${transaction.getSaleId()}`}
                            size="small"
                            startIcon={<ReceiptLongIcon />}
                          >
                            Venda #{transaction.getSaleId()}
                          </Button>
                        ) : (
                          transaction.getDescription()
                        )}
                      </TableCell>
                      <TableCell align="right">{formatBRL(transaction.getAmount())}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {transactions.length === 0 ? (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography color="text.secondary">
                  Nenhuma movimentação registrada.
                </Typography>
              </>
            ) : null}
          </CardContent>
        </Card>
      </Stack>
    </Shell>
  );
};
